import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { ToastConfirmationData, ToastConfirmationResult } from '../models/toast-confirmation.model';

@Injectable({
  providedIn: 'root'
})
export class ToastConfirmationService {
  private showToastSubject = new Subject<ToastConfirmationData>();
  private resultSubject = new Subject<ToastConfirmationResult>();

  public showToast$ = this.showToastSubject.asObservable();
  public result$ = this.resultSubject.asObservable();

  /**
   * Mostra um toast de confirmação e retorna um Observable com o resultado
   */
  confirm(data: ToastConfirmationData): Observable<ToastConfirmationResult> {
    this.showToastSubject.next({
      message: data.message,
      detail: data.detail,
      confirmText: data.confirmText || 'Sim!',
      cancelText: data.cancelText || 'Cancelar',
      okText: data.okText || 'OK',
      type: data.type || 'confirmation',
      autoClose: data.autoClose !== undefined ? data.autoClose : false,
      autoCloseDelay: data.autoCloseDelay || 3000,
      asModal: data.asModal
    });

    return this.result$;
  }

  /**
   * Mostra um toast de sucesso com botão OK.
   * Opcionalmente aceita autoClose (em ms) para fechar sozinho após o tempo.
   */
  success(message: string, options?: { autoCloseDelay?: number }): Observable<ToastConfirmationResult> {
    const autoCloseDelay = options?.autoCloseDelay ?? 0;
    return this.confirm({
      message,
      type: 'success',
      autoClose: autoCloseDelay > 0,
      autoCloseDelay,
      okText: 'OK'
    });
  }

  /**
   * Mostra um modal centralizado de sucesso (estilo SweetAlert) com botão OK
   */
  successModal(message: string): Observable<ToastConfirmationResult> {
    return this.confirm({
      message,
      type: 'success',
      autoClose: false,
      okText: 'OK',
      asModal: true
    });
  }

  /**
   * Mostra um toast de erro com botão OK
   */
  error(message: string): Observable<ToastConfirmationResult> {
    return this.confirm({
      message,
      type: 'error',
      autoClose: false,
      okText: 'OK'
    });
  }

  /**
   * Modal de erros de validação de questionário (OK + Cancelar)
   */
  validationError(message: string): Observable<ToastConfirmationResult> {
    return this.confirm({
      message,
      type: 'error',
      autoClose: false,
      okText: 'OK',
      cancelText: 'Cancelar'
    });
  }

  /**
   * Mostra um toast de informação com botão OK
   */
  info(message: string): Observable<ToastConfirmationResult> {
    return this.confirm({
      message,
      type: 'info',
      autoClose: false,
      okText: 'OK'
    });
  }

  /**
   * Mostra um toast de aviso (ex.: rate limit de envio de email)
   */
  warning(message: string): Observable<ToastConfirmationResult> {
    return this.confirm({
      message,
      type: 'warning',
      autoClose: false,
      okText: 'OK'
    });
  }

  /**
   * Confirma a ação (chamado pelo componente do toast)
   */
  confirmAction(): void {
    this.resultSubject.next({ confirmed: true, cancelled: false });
  }

  /**
   * Cancela a ação (chamado pelo componente do toast)
   */
  cancelAction(): void {
    this.resultSubject.next({ confirmed: false, cancelled: true });
  }

  /**
   * Fecha o toast (chamado pelo componente do toast)
   */
  closeToast(): void {
    this.resultSubject.next({ confirmed: false, cancelled: false });
  }
}
